import { useEffect, useState } from 'react';
import type {
  FetchRecommendationsDataArgs,
  RecommendationsServingConfig,
} from '@noths/polaris-client-listings-api';
import {
  fetchRecommendationsData,
  ListingsRequestAndFilterBuilder,
  ListingsRequestTextFilterBuilder,
  RecommendationsRequestBuilder,
} from '@noths/polaris-client-listings-api';
import { getUserAttributes } from '@noths/polaris-client-user-configuration';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { ListingsCarouselProduct } from 'src/adapters/listingsAPIToProductCarouselAdapter';
import { listingsAPIToProductCarouselAdapter } from 'src/adapters/listingsAPIToProductCarouselAdapter';
import { isProd } from 'src/environment';
import { logger } from 'src/utils/serverLogger';

const createListingsAPIFilter = (partnerShortCode?: string) => {
  const inStockFilter = new ListingsRequestTextFilterBuilder('availability', ['IN_STOCK']).build();
  const filterCollection = [inStockFilter];

  if (partnerShortCode) {
    const partnerFilter = new ListingsRequestTextFilterBuilder('attributes.partner_shortcode', [
      partnerShortCode,
    ]).build();
    filterCollection.push(partnerFilter);
  }

  return new ListingsRequestAndFilterBuilder().filters(filterCollection).build();
};

export interface UseListingsRecommendationsArgs {
  currencyCode: CurrencyCode;
  partnerShortCode: string;
  productCode: string;
  servingConfig: RecommendationsServingConfig;
  visitorId?: string;
}

type CreateRecommendationsParametersArgs = Pick<
  UseListingsRecommendationsArgs,
  'currencyCode' | 'productCode' | 'servingConfig' | 'visitorId' | 'partnerShortCode'
> & {
  userId?: string;
};

const createRecommendationsParameters = ({
  currencyCode,
  partnerShortCode,
  productCode,
  servingConfig,
  userId,
  visitorId,
}: CreateRecommendationsParametersArgs): FetchRecommendationsDataArgs => {
  const id = getProductIdFromProductCode(Number(productCode)).toString();
  const requestBuilder = new RecommendationsRequestBuilder();

  requestBuilder.currency(currencyCode);
  requestBuilder.pageSize(10);
  requestBuilder.products([{ id }]);

  const andFilter =
    servingConfig === 'pdp_same_seller_2'
      ? createListingsAPIFilter(partnerShortCode)
      : createListingsAPIFilter();

  requestBuilder.filter(andFilter);

  const body = requestBuilder.build();

  return {
    body,
    clientSide: true,
    env: isProd() ? 'prod' : 'qa',
    servingConfig,
    timeout: 15000,
    userId,
    visitorId,
  };
};

export const useListingsRecommendations = ({
  currencyCode,
  partnerShortCode,
  productCode,
  servingConfig,
  visitorId,
}: UseListingsRecommendationsArgs) => {
  const [recommendedProducts, setRecommendedProducts] = useState<ListingsCarouselProduct[] | null>(
    null,
  );

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setRecommendedProducts(null);
        const userAttributes = await getUserAttributes();
        const fetchParams = createRecommendationsParameters({
          currencyCode,
          partnerShortCode,
          productCode,
          servingConfig,
          userId: userAttributes?.sub,
          visitorId,
        });

        const responseData = await fetchRecommendationsData(fetchParams);

        if ('message' in responseData) {
          setRecommendedProducts([]);
          logger.error('Error getting recommendations from listings-api:', responseData.message);
          return;
        }

        const adaptedData = listingsAPIToProductCarouselAdapter(responseData);
        setRecommendedProducts(adaptedData.products);
      } catch (error) {
        setRecommendedProducts([]);
        logger.error('Exception thrown while getting recommendations from listings-api:', error);
      }
    };

    fetchProducts();
  }, [currencyCode, servingConfig, visitorId, partnerShortCode, productCode]);

  return {
    recommendedProducts,
  };
};
